/*
 * Cards (CMP-010 product, CMP-011 look/community) — Phase A1. No dependencies.
 * 1) Toggle buttons (.nc-toggle[data-nc-toggle]): favorite / like / save. Click flips aria-pressed at once
 *    (optimistic) and fires a bubbling, cancelable `nc-card:toggle` {kind, id, pressed}. preventDefault() rolls the
 *    change back. For async work the host calls NcCard.setState(button, 'loading' | 'error' | 'default', message);
 *    'error' restores the previous pressed value and announces the failure (never silently).
 * 2) Image errors: a failed <img> inside .nc-card__media marks the slot (data-media-error) and shows the fallback with
 *    a retry button; the slot keeps its aspect ratio. Retry reloads the image with a cache-busting query.
 */
(function () {
  if (window.NcCard) return;

  function liveRegion() {
    var el = document.getElementById('nc-card-live');
    if (!el) {
      el = document.createElement('div');
      el.id = 'nc-card-live';
      el.className = 'nc-visually-hidden';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    return el;
  }

  function formatCount(n) {
    if (n >= 1000000) return (Math.round(n / 100000) / 10).toString().replace('.', ',') + 'M';
    if (n >= 1000) return (Math.round(n / 100) / 10).toString().replace('.', ',') + 'K';
    return String(n);
  }

  function applyCount(button, pressed) {
    var out = button.querySelector('[data-nc-count]');
    if (!out) return;
    var base = parseInt(button.getAttribute('data-count') || '0', 10);
    var shown = base + (pressed ? 1 : 0) - (button.getAttribute('data-initial-pressed') === 'true' ? 1 : 0);
    out.textContent = formatCount(Math.max(0, shown));
    syncLabel(button);
  }

  // like buttons: the accessible name starts with the visible count ("1,2K me gusta: @handle", WCAG 2.5.3). The first word of the
  // aria-label is the count; it is rewritten from the text on screen, so it also follows renderers that fill the count themselves
  function syncLabel(button) {
    var out = button && button.querySelector('[data-nc-count]');
    var label = button && button.getAttribute('aria-label');
    if (!out || label === null) return;
    var next = label.replace(/^\S*/, out.textContent.trim());
    if (next !== label) button.setAttribute('aria-label', next);
  }

  var NcCard = {
    setState: function (button, state, message) {
      if (state === 'error') {
        var prev = button.getAttribute('data-prev-pressed');
        if (prev !== null) {
          button.setAttribute('aria-pressed', prev);
          applyCount(button, prev === 'true');
        }
        button.setAttribute('data-state', 'error');
        button.removeAttribute('aria-busy');
        liveRegion().textContent = message || button.getAttribute('data-error-label') || 'No se pudo guardar';
      } else if (state === 'loading') {
        button.setAttribute('data-state', 'loading');
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('data-state');
        button.removeAttribute('aria-busy');
        button.removeAttribute('data-prev-pressed');
      }
    }
  };
  window.NcCard = NcCard;

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.nc-toggle[data-nc-toggle]');
    if (button) {
      if (button.getAttribute('data-state') === 'loading') return;
      var was = button.getAttribute('aria-pressed') === 'true';
      button.setAttribute('data-prev-pressed', was ? 'true' : 'false');
      button.removeAttribute('data-state');
      button.setAttribute('aria-pressed', was ? 'false' : 'true');
      applyCount(button, !was);
      var ok = button.dispatchEvent(new CustomEvent('nc-card:toggle', {
        bubbles: true,
        cancelable: true,
        detail: { kind: button.getAttribute('data-nc-toggle'), id: button.getAttribute('data-id'), pressed: !was }
      }));
      if (!ok) {
        button.setAttribute('aria-pressed', was ? 'true' : 'false');
        applyCount(button, was);
      }
      return;
    }
    var retry = event.target.closest && event.target.closest('.nc-card__retry');
    if (retry) {
      var media = retry.closest('.nc-card__media');
      var img = media && media.querySelector('img');
      if (!img) return;
      media.removeAttribute('data-media-error');
      var src = img.getAttribute('src') || '';
      img.removeAttribute('srcset'); // a responsive candidate that failed would be picked again
      img.setAttribute('src', src + (src.indexOf('?') < 0 ? '?' : '&') + 'retry=' + Date.now());
    }
  });

  // error events do not bubble: listen in the capture phase
  document.addEventListener('error', function (event) {
    var img = event.target;
    if (!img || img.tagName !== 'IMG') return;
    var media = img.closest('.nc-card__media');
    if (media) media.setAttribute('data-media-error', '');
  }, true);

  // initial counts use the same abbreviation as later updates ("1200" -> "1,2K")
  document.querySelectorAll('.nc-toggle[data-count] [data-nc-count]').forEach(function (out) {
    out.textContent = formatCount(parseInt(out.closest('.nc-toggle').getAttribute('data-count') || '0', 10));
    syncLabel(out.closest('.nc-toggle'));
  });

  // cards cloned from templates (home, Marcas, comunidad, perfil) get their count after this script ran: keep their name in step
  new MutationObserver(function (records) {
    records.forEach(function (r) {
      var el = r.target.nodeType === 1 ? r.target : r.target.parentElement;
      var own = el && el.closest('.nc-toggle--like');
      if (own) syncLabel(own);
      Array.prototype.forEach.call(r.addedNodes || [], function (n) {
        if (n.nodeType === 1) n.querySelectorAll('.nc-toggle--like').forEach(syncLabel);
      });
    });
  }).observe(document.documentElement, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['aria-label'] });

  // images that failed before this script ran
  document.querySelectorAll('.nc-card__media img').forEach(function (img) {
    if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) {
      img.closest('.nc-card__media').setAttribute('data-media-error', '');
    }
  });
})();

/*
 * The ONE heart -- selection animation trigger (CSS: assets/nc-heart.css). Watches every `.nc-heart` button and, when its aria-pressed
 * flips because of a click or key press (a user gesture within the last 1.5 s, which also covers a gateway answer), adds `is-popping`
 * (false -> true) or `is-releasing` (true -> false) for the length of the animation. A pressed state set while a page or a card is
 * rendered is NOT a gesture, so nothing animates on first paint. It changes no state and fires no events. No dependencies.
 */
(function () {
  if (typeof document === 'undefined' || window.NcHeart) return;
  var WINDOW_MS = 1500;
  var POP_MS = 460;
  var RELEASE_MS = 220;
  var touched = new WeakMap();

  function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }

  function mark(event) {
    var el = event.target && event.target.closest && event.target.closest('.nc-heart');
    if (el) touched.set(el, now());
  }
  document.addEventListener('click', mark, true);
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' || event.key === ' ') mark(event);
  }, true);

  function play(el, cls, ms) {
    el.classList.remove('is-popping', 'is-releasing');
    void el.offsetWidth; // restart the animation when it is already running
    el.classList.add(cls);
    window.clearTimeout(el.__ncHeartTimer);
    el.__ncHeartTimer = window.setTimeout(function () { el.classList.remove(cls); }, ms);
  }

  var observer = new MutationObserver(function (records) {
    records.forEach(function (rec) {
      var el = rec.target;
      if (!el.classList || !el.classList.contains('nc-heart')) return;
      var next = el.getAttribute('aria-pressed');
      if (rec.oldValue === next || (next !== 'true' && next !== 'false')) return;
      var at = touched.get(el);
      if (at === undefined || now() - at > WINDOW_MS) return;
      if (next === 'true') play(el, 'is-popping', POP_MS);
      else if (rec.oldValue === 'true') play(el, 'is-releasing', RELEASE_MS);
    });
  });
  observer.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'], attributeOldValue: true });

  window.NcHeart = { play: play };
})();
